import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useItemSearch } from "../Context/ItemSearchContext";
import { getAllCategories } from "../actions/categoryActions";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { CART_DRAWER_WIDTH, APP_FOOTER_HEIGHT, APP_FOOTER_HEIGHT_XS } from "./cartLayout";


const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    // Sit on top of the global Footer (clock / timezone / Mindtree) instead
    // of covering it at bottom: 0.
    bottom: APP_FOOTER_HEIGHT,
    left: 0,
    width: "100%",
    zIndex: 1000,
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "width 0.3s ease, left 0.3s ease",
    boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
    borderTop: "1px solid #e0e0e0",
    padding: theme.spacing(0.5, 0),
    [theme.breakpoints.down("xs")]: {
      bottom: APP_FOOTER_HEIGHT_XS,
    },
  },
  list: {
    display: "flex",
    overflowX: "auto",
    padding: theme.spacing(0.5, 1),
    flex: 1,
    "&::-webkit-scrollbar": {
      height: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "2px",
      "&:hover": {
        background: "#555",
      },
    },
    scrollbarWidth: "thin",
    scrollbarColor: "#888 #f1f1f1",
  },
  listItem: {
    marginRight: theme.spacing(1.5),
    fontWeight: 500,
    backgroundColor: "white",
    textAlign: "center",
    minWidth: "auto",
    flexShrink: 0,
    whiteSpace: "nowrap",
    borderRadius: "24px",
    border: "2px solid #e0e0e0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    padding: theme.spacing(0.75, 2),
    "&:hover": {
      borderColor: "rgb(239, 127, 26)",
      backgroundColor: "#fff5e6",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.5, 1.25),
      marginRight: theme.spacing(1),
      fontSize: "0.8rem",
    },
  },
  selectedItem: {
    backgroundColor: "rgb(239, 127, 26)",
    color: "white",
    border: "2px solid rgb(239, 127, 26)",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "rgb(220, 110, 20)",
      borderColor: "rgb(220, 110, 20)",
    },
  },
  arrowIcons: {
    backgroundColor: "rgb(239, 127, 26)",
    "&:hover": {
      backgroundColor: "rgb(220, 110, 20)",
    },
  },
}));

const FloatingFooter = ({ open, isCartPersistent }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.categories);
  const { selectedCategory, setSelectedCategory } = useItemSearch();
  const repeatTimer = useRef(null);
  const repeatInterval = useRef(null);

  const scrollCategories = (distance, behavior = "smooth") => {
    const list = document.getElementById("categoryList");
    if (list) {
      list.scrollBy({
        left: distance,
        behavior,
      });
    }
  };

  const stopScrolling = () => {
    window.clearTimeout(repeatTimer.current);
    window.clearInterval(repeatInterval.current);
    repeatTimer.current = null;
    repeatInterval.current = null;
  };

  const startScrolling = (distance) => {
    stopScrolling();
    // Wait briefly so a normal click remains a single smooth step; holding
    // the button then switches to fast, predictable paging.
    repeatTimer.current = window.setTimeout(() => {
      repeatInterval.current = window.setInterval(
        () => scrollCategories(distance, "auto"),
        85
      );
    }, 280);
  };

  useEffect(
    () => () => {
      window.clearTimeout(repeatTimer.current);
      window.clearInterval(repeatInterval.current);
    },
    []
  );
  useEffect(() => {
    // `categories` (the array reference) must NOT be a dependency here: an
    // empty catalog means every fetch resolves to a new-but-still-empty
    // array, which would re-trigger this effect forever - a runaway
    // /categories request loop, not just a one-time "fetch until non-empty".
    if (!categories.length) {
      dispatch(getAllCategories());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  const sortedCategories = [...categories]
    .filter((category) => !category.isDeleted && category.isActive !== false)
    .sort((first, second) =>
      (first.categoryName || "").localeCompare(second.categoryName || "", undefined, {
        sensitivity: "base",
      })
    );
  return (
    <Paper
      className={classes.root}
      elevation={0}
      style={{
        width: open && isCartPersistent ? `calc(100% - ${CART_DRAWER_WIDTH}px)` : "100%",
        left: 0,
      }}
    >
      <IconButton
        onClick={() => scrollCategories(-150)}
        onMouseDown={() => startScrolling(-150)}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling(-150)}
        onTouchEnd={stopScrolling}
        onTouchCancel={stopScrolling}
        aria-label="Scroll categories left"
        className={classes.arrowIcons}
        size={isMobile ? "small" : "medium"}
        style={{
          marginLeft: theme.spacing(1),
          color: "white",
          padding: theme.spacing(isMobile ? 0.5 : 1),
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <List id="categoryList" className={classes.list}>
        <ListItem
          className={`${classes.listItem} ${selectedCategory === null ? classes.selectedItem : ''}`}
          style={{ width: "auto", justifyContent: "center" }}
          onClick={() => setSelectedCategory(null)}
        >
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            All
          </Typography>
        </ListItem>
        {sortedCategories.map((category) => (
          <ListItem
            key={category._id}
            className={`${classes.listItem} ${selectedCategory === category ? classes.selectedItem : ''}`}
            style={{ width: "auto", justifyContent: "center" }}
            onClick={() => setSelectedCategory(category)}
          >
            <ListItemText 
              primary={category.categoryName} 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: selectedCategory === category ? 600 : 500,
                  textAlign: "center"
                } 
              }}
            />
          </ListItem>
        ))}
      </List>
      <IconButton
        onClick={() => scrollCategories(150)}
        onMouseDown={() => startScrolling(150)}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling(150)}
        onTouchEnd={stopScrolling}
        onTouchCancel={stopScrolling}
        aria-label="Scroll categories right"
        className={classes.arrowIcons}
        size={isMobile ? "small" : "medium"}
        style={{
          marginRight: theme.spacing(1),
          color: "white",
          padding: theme.spacing(isMobile ? 0.5 : 1),
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Paper>
  );
};

export default FloatingFooter;
